import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";

import type { TrackedProperty } from "../../lib/dashboard";
import { isRelationshipStage } from "../../lib/relationship-stage";
import type {
  CheckinResponse,
  MyReviewState,
} from "../../lib/marketplace-connection-api";
import type {
  CheckinCardDTO,
  ConnectedPayoffState,
  NoContactExit,
} from "../../lib/marketplace-status";
import {
  reviewAskIsOpen,
  reviewAskView,
  type ReviewAnswer,
} from "../../lib/review-ask";
import { AddPropertyWidget } from "../dashboard/journey-widgets/add-property-widget";
import { AcceptedOfferCard } from "../marketplace/AcceptedOfferCard";
import { CheckinCard } from "../marketplace/CheckinCard";
import type {
  DisconnectHandler,
  DisconnectParams,
} from "../marketplace/DisconnectMarketplaceDialog";
import { NoContactExitBanner } from "../marketplace/NoContactExitBanner";
import { RelationshipStageTrack } from "../marketplace/RelationshipStageTrack";
import { SectionLabel } from "../marketplace/OffersInbox";
import { Button } from "../ui/button";
import { ReviewPrompt } from "./ReviewPrompt";

/**
 * ConnectedStack — the `connected` body block (plan 435 M4, mfh-connected-
 * stack; roadmap M4 bullet 2; tech-spec Components "ConnectedStack").
 *
 * Only ever mounted by `HomeView` for a `connected-payoff` card state
 * (status === "active" by construction of `deriveMarketplaceCardState`), so
 * the contact row below is defense-in-depth on top of an already-active-
 * only type, mirroring `MarketplaceStatusCard`'s `ConnectedPayoffCard`
 * invariant exactly — no second `status` check is threaded through, the
 * type itself is the guarantee.
 *
 * Right-column precedence (roadmap M4 bullets 3 and 4): check-in (when
 * `checkin.pending`) > the review prompt (while the server offers a review
 * ask, `next_ask`) > `AddPropertyWidget`. Exactly one of the three ever
 * renders.
 */
export interface ConnectedStackProps {
  connection: ConnectedPayoffState;
  /** ISO timestamp the connection was activated — absent (no wire field
   *  carries this yet) degrades the headline to drop the date clause
   *  rather than fabricate one. See the M4 handoff's residual-risk note. */
  connectedAt?: string;
  checkin?: CheckinCardDTO;
  getToken: () => Promise<string | null>;
  /** The connection's own role ("agent") — threaded through to the
   *  check-in POST and the disconnect POST exactly like
   *  `useMarketplaceConnection`'s read. */
  role: string;
  onCheckinAnswered?: (response: CheckinResponse) => void;
  onDisconnect: (params: DisconnectParams) => Promise<void>;
  /** Plan 462 M5: the server's `no_contact_exit` block for this connection.
   *  The banner renders from `eligible` alone; absent reads as ineligible. */
  noContactExit?: NoContactExit;
  /** Disconnects from the no-contact banner and RETURNS the result (the done
   *  state reads `republished`). Must not refresh the connection read: that
   *  would unmount the dialog before the buyer sees the outcome. */
  onNoContactExitDisconnect?: DisconnectHandler;
  /** Fires when the banner's dialog closes after a completed disconnect —
   *  the caller refreshes the connection read here. */
  onNoContactExitEnded?: () => void;
  incentiveLabel?: string;
  /** The server's review state for this pro — `undefined` while the read is
   *  in flight/unknown (right column defaults to `AddPropertyWidget` until
   *  resolved, never flashing the review prompt speculatively). The prompt
   *  renders only while `next_ask` offers an ask. */
  reviewState?: MyReviewState;
  onSubmitReview?: (answer: ReviewAnswer) => Promise<void>;
  trackedProperties: readonly TrackedProperty[];
  onAddProperty: () => void;
}

function formatConnectedDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function ConnectedStack({
  connection,
  connectedAt,
  checkin,
  getToken,
  role,
  onCheckinAnswered,
  onDisconnect,
  noContactExit,
  onNoContactExitDisconnect,
  onNoContactExitEnded,
  incentiveLabel,
  reviewState,
  onSubmitReview,
  trackedProperties,
  onAddProperty,
}: ConnectedStackProps) {
  const agentName = connection.proName.trim() || "your agent";
  const connectedDate = formatConnectedDate(connectedAt);
  const connectionSource = connection.acceptedOffer
    ? "Matched through Sill Marketplace"
    : "Connected through Sill";
  const messageHref = connection.proUserId
    ? `/threads?pro=${connection.proUserId}`
    : "/threads";

  // Sticky once answered (the OffersPage idiom): answering refreshes the
  // connection, which flips `checkin.pending` false — without this latch the
  // right column swapped to the review prompt / next move mid-answer and
  // unmounted the CheckinCard's own close-upgrade review modal the instant
  // it opened (2026-10-09: "something flashed then went away").
  const [checkinAnswered, setCheckinAnswered] = useState(false);
  // Same latch for the review prompt: a submit refetches the review state,
  // which closes the ask. Keeping the prompt mounted keeps its confirmation
  // ("your review is posted") on screen instead of swapping it away.
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const showReviewPrompt =
    Boolean(onSubmitReview && connection.proUserId) &&
    (reviewAskIsOpen(reviewAskView(reviewState)) || reviewSubmitted);

  const rightColumn =
    checkin?.pending || checkinAnswered ? (
      <div data-testid="home-checkin">
        <CheckinCard
          agentName={agentName}
          checkin={checkin ?? { pending: false }}
          connectionId={connection.connectionId}
          getToken={getToken}
          incentiveLabel={incentiveLabel}
          onAnswered={(response) => {
            setCheckinAnswered(true);
            onCheckinAnswered?.(response);
          }}
          onDisconnect={onDisconnect}
          proUserId={connection.proUserId}
          role={role}
        />
      </div>
    ) : showReviewPrompt && onSubmitReview && connection.proUserId ? (
      <ReviewPrompt
        incentiveLabel={incentiveLabel}
        onSubmit={async (answer) => {
          // Latch only on success: a refused or failed write leaves the ask
          // (or the read-only review) exactly as the server says.
          await onSubmitReview(answer);
          setReviewSubmitted(true);
        }}
        proName={agentName}
        proUserId={connection.proUserId}
        role={role}
        state={reviewState}
      />
    ) : (
      <div
        className="marketplace-surface space-y-3 p-4 sm:p-5"
        data-testid="connected-stack-next-move"
      >
        <SectionLabel label="Your next move" />
        <AddPropertyWidget
          onAddProperty={onAddProperty}
          trackedProperties={trackedProperties}
        />
      </div>
    );

  return (
    <section
      className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_300px]"
      data-testid="home-connected-stack"
    >
      {/* Where the relationship stands, full width directly under the hero
          (2026-10-09). The agent card below used to repeat "Connected" three
          times (eyebrow, connected line, stage chip); the stage now lives
          here only, and the card just names the agent. */}
      {connection.relationshipStage &&
      isRelationshipStage(connection.relationshipStage) ? (
        <div
          className="marketplace-surface p-5 sm:col-span-2 sm:p-6"
          data-testid="connected-stack-stage"
        >
          <RelationshipStageTrack
            action={
              connection.proUserId ? (
                <Link
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  data-testid="connected-stack-stage-link"
                  to={`/team/${connection.proUserId}`}
                >
                  Details
                  <ArrowRight aria-hidden className="size-3.5" />
                </Link>
              ) : undefined
            }
            connectedAt={connectedAt}
            currentStage={connection.relationshipStage}
            eyebrow="Your home search"
          />
        </div>
      ) : null}

      {noContactExit?.eligible && onNoContactExitDisconnect ? (
        <div
          className="sm:col-span-2"
          data-testid="connected-stack-no-contact-exit"
        >
          <NoContactExitBanner
            connectionId={connection.connectionId}
            noContactExit={noContactExit}
            onDisconnect={onNoContactExitDisconnect}
            onEnded={onNoContactExitEnded}
            proName={agentName}
            role={role}
          />
        </div>
      ) : null}

      <div
        className="marketplace-surface space-y-4 p-5"
        data-testid="connected-stack-agent-card"
      >
        <div>
          <p className="eyebrow-label">Your agent</p>
          <p
            className="mt-1 text-lg font-bold text-foreground"
            data-testid="connected-stack-pro-name"
          >
            {connection.proName.trim() || "Your agent"}
          </p>
          <p
            className="text-sm text-foreground/70"
            data-testid="connected-stack-connected-line"
          >
            {connectedDate
              ? `${connectionSource} · ${connectedDate}`
              : connectionSource}
          </p>
        </div>

        {/* Contact row — the ONLY place on Home that ever renders Message/
            email/phone. Gated structurally: this component only ever mounts
            for a connected-payoff card state. */}
        <div
          className="flex flex-wrap items-center gap-2 rounded-xl bg-muted/40 p-3"
          data-testid="connected-stack-contact-row"
        >
          <Button asChild data-testid="connected-stack-message" size="sm">
            <Link to={messageHref}>Message {agentName}</Link>
          </Button>
          {connection.proContact ? (
            <span
              className="text-sm text-muted-foreground"
              data-testid="connected-stack-contact"
            >
              {connection.proContact}
            </span>
          ) : null}
        </div>

        {connection.acceptedOffer ? (
          <AcceptedOfferCard offer={connection.acceptedOffer} />
        ) : connection.agreedTermsSummary ? (
          <div
            className="flex items-center gap-2 text-sm text-foreground"
            data-testid="connected-stack-terms-summary"
          >
            <BadgeCheck
              aria-hidden="true"
              className="size-4 shrink-0 text-primary"
            />
            <span>{connection.agreedTermsSummary}</span>
          </div>
        ) : null}
      </div>

      <div data-testid="connected-stack-right-column">{rightColumn}</div>
    </section>
  );
}
